import React, { useState, useRef, useEffect, useCallback } from "react";
import FeatherIcon from "./FeatherIcons"; // Assuming FeatherIcons is correctly imported
import { formatDate } from "../utils/dateUtils";
import { useTheme } from "../components/ThemeProvider";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faGear,
  faUser,
  faChevronUp,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  ListChecks,
  NotebookText,
  Clock,
  Mic,
  MicOff,
  Sidebar as SidebarIcon,
  Calendar as CalendarIcon,
  Search as SearchIcon,
} from "lucide-react";
import AssistantWrapper from "./Tiptap/extensions/AssistantWrapper";
import AutoWakeWordLoader from "./AutoWakeWordLoader";

const AppFooter = ({
  toggleSidebar,
  toggleCalendarVisibility,
  toggleSearchVisibility,
  selectedDate,
  hasUnsavedChanges,
  isLoading,
  handleDateClick,
  reloadContent,
  isSidebarOpen,
  sidebarWidth,
  showSidebarToggle = true,
  showCalendar = true,
  showSearch = true,
  showCompletedTasks = true,
  showUserDropdown = true,
  showJournal = false,
  showassistant = false, // Manual Assistant visibility trigger
  showTimeLine = false,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const assistantWrapperRef = useRef(null); // Ref to AssistantWrapper instance (kept for potential future use, but not directly used for startListening)
  const assistantRef = useRef(null); // Ref to the actual Assistant component instance inside Wrapper
  const audioPlaybackRef = useRef(null); // Ref to manage the audio object instance for hello.wav

  // --- State Management ---
  const [wakeWordModelLoaded, setWakeWordModelLoaded] = useState(false);
  // 'wakeWordEnabled' controls if AutoWakeWordLoader *processes* audio
  const [wakeWordEnabled, setWakeWordEnabled] = useState(true);
  // Controls visibility of the Assistant UI (TaskPlexer)
  const [showTaskPlexer, setShowTaskPlexer] = useState(false);
  // Flag to trigger Assistant listening sequence AFTER audio playback
  const [assistantShouldListen, setAssistantShouldListen] = useState(false);
  // Store stream in an object wrapper to trigger effects reliably
  const [streamInfo, setStreamInfo] = useState({
    stream: null,
    timestamp: null,
  });
  const currentStreamRef = useRef(null); // Direct access to the latest stream

  // --- Theme & Class Names (Unchanged) ---
  const navbarClass =
    theme === "light"
      ? "bg-light"
      : theme === "dark"
      ? "bg-dark"
      : "bg-purple-900"; // Assuming bg-purple-900 is defined in CSS/Tailwind
  const textClass = theme === "light" ? "text-dark" : "text-light";
  const navItems = [
    { name: "Settings", path: "/settings", icon: faGear },
    { name: "Account", path: "/account", icon: faUser },
    { name: "Support", path: "/support", icon: faQuestionCircle },
  ];

  // --- Handlers (Unchanged unless specified) ---
  const handleLogout = async () => {
    await logout();
    navigate("/login", { state: { fromLogout: true } });
  };

  const toggleDropdown = () => {
    if (!dropdownOpen) {
      setDropdownOpen(true);
      setTimeout(() => setDropdownVisible(true), 50);
    } else {
      setDropdownVisible(false);
      setTimeout(() => setDropdownOpen(false), 300);
    }
  };

  // --- Effects (Unchanged unless specified) ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore clicks on the microphone button or its container
      const micButtonEl = document.querySelector(
        '[aria-label*="Wake Word Processing"]'
      );
      if (
        micButtonEl &&
        (micButtonEl === event.target || micButtonEl.contains(event.target))
      ) {
        return; // Skip processing for microphone button clicks
      }

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        toggleRef.current && // Check if toggleRef exists
        !toggleRef.current.contains(event.target)
      ) {
        setDropdownVisible(false);
        setTimeout(() => setDropdownOpen(false), 300);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // Dependency array is correct

  // --- Callback passed to AutoWakeWordLoader (MODIFIED FOR TRACK ENABLE) ---
  const handleStreamReady = useCallback((stream) => {
    // console.log(">>> AppFooter: handleStreamReady called.");
    if (stream && stream.active) {
      // Added active check
      // console.log(">>> AppFooter: Received active shared MediaStream:", stream);
      currentStreamRef.current = stream;
      setStreamInfo({ stream: stream, timestamp: Date.now() });
      // Ensure tracks are enabled by default when stream is ready
      stream.getAudioTracks().forEach((track) => (track.enabled = true));
      // console.log(
      //   ">>> AppFooter: Called setStreamInfo. Mic should be initially enabled."
      // );
    } else {
      console.log(
        ">>> AppFooter: Shared MediaStream received as null or inactive."
      );
      if (currentStreamRef.current) {
        // If we previously had a stream, make sure to nullify refs/state
        currentStreamRef.current = null;
        setStreamInfo({ stream: null, timestamp: Date.now() });
        // console.log(">>> AppFooter: Cleared previous stream info.");
      }
    }
  }, []); // Empty dependency array is correct

  // --- Callback passed to AutoWakeWordLoader (MODIFIED FOR AUDIO PLAYBACK) ---
  const handleWakeWordDetected = useCallback(
    (confidence) => {
      console.log(
        `AppFooter: handleWakeWordDetected called (Confidence: ${confidence.toFixed(
          4
        )}).`
      );

      // Ensure previous audio playback is stopped/cleaned up if somehow interrupted
      if (audioPlaybackRef.current) {
        console.warn(
          "AppFooter: Overlapping wake word detection? Stopping previous audio."
        );
        audioPlaybackRef.current.pause();
        audioPlaybackRef.current.onended = null; // Remove listeners
        audioPlaybackRef.current.onerror = null;
        audioPlaybackRef.current = null;
        // Attempt to unmute just in case it was left muted
        if (currentStreamRef.current && currentStreamRef.current.active) {
          currentStreamRef.current.getAudioTracks().forEach((track) => {
            if (!track.enabled) {
              console.log(
                "AppFooter: Unmuting mic after stopping overlapping audio."
              );
              track.enabled = true;
            }
          });
        }
      }

      const currentStream = currentStreamRef.current;

      if (!wakeWordEnabled) {
        console.log(
          "AppFooter: Wake word detected, but processing is disabled. Ignoring."
        );
        return;
      }

      // Check the stream from the ref
      if (!currentStream || !currentStream.active) {
        console.error(
          "AppFooter: Wake word detected, but stream is missing or inactive! Cannot play sound or start assistant."
        );
        // Optionally: Show an error message to the user?
        return;
      }

      console.log(
        "AppFooter: Wake word detected. Disabling wake word, showing TaskPlexer, preparing to play sound."
      );
      // --- Immediate Actions ---
      setShowTaskPlexer(true); // Show UI feedback
      setWakeWordEnabled(false); // Stop wake word detection loop

      // --- Audio Playback Sequence ---
      try {
        // 1. Mute the microphone stream tracks
        console.log("AppFooter: Muting microphone tracks.");
        currentStream
          .getAudioTracks()
          .forEach((track) => (track.enabled = false));

        // 2. Prepare the audio element
        const audio = new Audio("/hello.wav"); // Ensure this path is correct relative to your public folder
        audio.preload = "auto";
        audioPlaybackRef.current = audio; // Store instance in ref

        // Function to unmute and start listening (to avoid repetition)
        const unmuteAndStartListening = (source) => {
          console.log(
            `AppFooter: ${source} - Attempting to unmute and start listening.`
          );
          audioPlaybackRef.current = null; // Clear the ref
          // Check stream validity again *before* unmuting/starting
          const stream = currentStreamRef.current;
          if (stream && stream.active) {
            console.log("AppFooter: Unmuting microphone tracks.");
            stream.getAudioTracks().forEach((track) => (track.enabled = true));
            // Signal the assistant to start listening AFTER unmuting
            console.log("AppFooter: Setting assistantShouldListen to true.");
            setAssistantShouldListen(true); // Trigger the useEffect hook
          } else {
            console.warn(
              "AppFooter: Stream became invalid before unmuting/starting assistant after audio playback.",
              { source }
            );
            // Handle error case: hide TaskPlexer, re-enable wake word
            setShowTaskPlexer(false);
            setWakeWordEnabled(true);
            setAssistantShouldListen(false); // Ensure it's false
          }
        };

        // 3. Define what happens when the sound finishes
        audio.onended = () => {
          console.log("AppFooter: /hello.wav finished playing.");
          unmuteAndStartListening("onended");
        };

        // 4. Define error handling for audio playback
        audio.onerror = (e) => {
          console.error("AppFooter: Error playing /public/hello.wav:", e);
          // Attempt to unmute and start listening anyway
          unmuteAndStartListening("onerror");
        };

        // 5. Play the audio
        console.log("AppFooter: Playing /hello.wav...");
        audio.play().catch((playError) => {
          // Catch potential errors from the play() promise itself (e.g., user interaction required)
          console.error("AppFooter: audio.play() promise rejected:", playError);
          // Manually trigger the onerror logic if play() fails immediately
          if (audio.onerror) {
            // Check if the audio object still exists in the ref before calling onerror
            if (audioPlaybackRef.current === audio) {
              console.log(
                "AppFooter: Manually triggering onerror logic due to play() rejection."
              );
              // Simulate error event or directly call the logic
              audio.onerror({ type: "play_error", error: playError });
            } else {
              console.warn(
                "AppFooter: Audio object changed before play() rejection could be handled."
              );
            }
          }
        });
      } catch (error) {
        console.error("AppFooter: Error during audio playback setup:", error);
        audioPlaybackRef.current = null; // Clear ref on setup error
        // Attempt to unmute if muting happened and stream exists
        if (currentStream && currentStream.active) {
          try {
            currentStream.getAudioTracks().forEach((track) => {
              if (!track.enabled) {
                // Only unmute if it was muted
                track.enabled = true;
              }
            });
            console.log(
              "AppFooter: Unmuted microphone tracks after setup error."
            );
          } catch (unmuteError) {
            console.error(
              "AppFooter: Error unmuting mic after setup error:",
              unmuteError
            );
          }
        }
        // Reset state as the sequence failed
        setShowTaskPlexer(false);
        setWakeWordEnabled(true); // Re-enable wake word
        setAssistantShouldListen(false); // Ensure signal is off
        console.log("AppFooter: Resetting state after audio setup error.");
      }

      // --- IMPORTANT ---
      // setAssistantShouldListen(true) is now ONLY called within the
      // audio.onended or audio.onerror handlers (via unmuteAndStartListening).
    },
    [wakeWordEnabled] // Dependencies: only wakeWordEnabled needed directly here.
    // streamInfo/currentStreamRef are accessed via refs.
    // setAssistantShouldListen, setShowTaskPlexer, setWakeWordEnabled are state setters.
  );

  // --- Callback passed to AssistantWrapper/Assistant (MODIFIED FOR AUDIO CLEANUP) ---
  const handleMicrophoneReleased = useCallback(() => {
    console.log("AppFooter: Microphone control released by Assistant.");

    // Stop any lingering audio playback from hello.wav if assistant finished quickly/was interrupted
    if (audioPlaybackRef.current) {
      console.warn(
        "AppFooter: Assistant released mic while hello.wav might still be playing/pending. Stopping audio."
      );
      audioPlaybackRef.current.pause();
      audioPlaybackRef.current.onended = null;
      audioPlaybackRef.current.onerror = null;
      audioPlaybackRef.current = null;
      // Ensure mic is unmuted as assistant might have closed before onended/onerror fired
      const stream = currentStreamRef.current;
      if (stream && stream.active) {
        stream.getAudioTracks().forEach((track) => {
          if (!track.enabled) {
            console.log(
              "AppFooter: Unmuting mic on assistant release (fallback)."
            );
            track.enabled = true;
          }
        });
      }
    }

    // 1. Re-enable wake word *processing*
    // Check if component is still mounted before setting state
    // (This check might be overly cautious depending on exact component lifecycle)
    // if (document.contains(dropdownRef.current)) { // Example check, might need refinement
    setWakeWordEnabled(true);
    console.log("AppFooter: Wake word processing enabled (resumed).");
    // } else {
    //     console.log("AppFooter: Component unmounted before re-enabling wake word.");
    // }

    // 2. Ensure assistant listening signal is off
    setAssistantShouldListen(false);

    // 3. Optionally hide TaskPlexer if desired after assistant finishes
    // setShowTaskPlexer(false); // Keep TaskPlexer open after interaction for now
  }, []); // No direct dependencies, relies on refs and state setters

  // --- Effect to Start Assistant Listening (MODIFIED WITH SAFEGUARDS) ---
  useEffect(() => {
    const currentStream = streamInfo.stream; // Get stream from state

    // Log status including check based on the derived stream
    // console.log(">>> AppFooter: Assistant startListening Effect triggered.", {
    //   assistantShouldListen,
    //   hasStream: !!currentStream, // Check based on stream from streamInfo
    //   isStreamActive: !!currentStream?.active,
    //   hasAssistantRef: !!assistantRef.current,
    // });

    if (
      assistantShouldListen &&
      currentStream &&
      currentStream.active &&
      assistantRef.current
    ) {
      // CRITICAL: Ensure stream tracks are enabled before passing to assistant
      // This is a safeguard, should already be handled by unmuteAndStartListening
      let tracksWereDisabled = false;
      currentStream.getAudioTracks().forEach((track) => {
        if (!track.enabled) {
          console.warn(
            "AppFooter: Track was disabled when attempting to start assistant! Re-enabling.",
            track
          );
          track.enabled = true; // Force enable
          tracksWereDisabled = true;
        }
      });
      if (tracksWereDisabled) {
        console.log(
          "AppFooter: Forced track enable before calling startListening."
        );
      }

      // console.log(
      //   ">>> AppFooter: Calling assistantRef.current.startListening with stream:",
      //   currentStream
      // );
      try {
        // Ensure the method exists before calling
        if (typeof assistantRef.current.startListening === "function") {
          assistantRef.current.startListening(currentStream);
          // Reset the signal immediately after *successfully* calling startListening
          setAssistantShouldListen(false);
          // console.log(
          //   ">>> AppFooter: Reset assistantShouldListen after calling startListening."
          // );
        } else {
          console.error(
            "AppFooter: assistantRef.current.startListening is not a function!"
          );
          // Handle error: maybe hide TaskPlexer, re-enable wake word?
          setAssistantShouldListen(false); // Still reset signal
          setShowTaskPlexer(false);
          setWakeWordEnabled(true); // Try to recover
        }
      } catch (error) {
        console.error(
          "AppFooter: Error calling Assistant's startListening:",
          error
        );
        setAssistantShouldListen(false); // Reset signal on error too
        setShowTaskPlexer(false);
        setWakeWordEnabled(true); // Try to recover
      }
    } else if (assistantShouldListen) {
      // Log condition if signal is true but prerequisites are missing
      console.warn(
        "AppFooter: assistantShouldListen is true, but prerequisites missing or invalid.",
        {
          hasStream: !!currentStream,
          isStreamActive: !!currentStream?.active,
          hasAssistantRef: !!assistantRef.current,
          isAssistantFuncAvailable:
            typeof assistantRef.current?.startListening === "function",
        }
      );
      // Reset the signal to prevent loops if conditions don't resolve
      setAssistantShouldListen(false);
      // Maybe show error state or attempt recovery
      setShowTaskPlexer(false);
      setWakeWordEnabled(true); // Re-enable wake word if assistant couldn't start
    }
    // Dependency array remains the same: trigger when signal or stream info changes.
  }, [assistantShouldListen, streamInfo]); // Depend on streamInfo object

  // --- Get Assistant Ref from Wrapper ---
  const handleAssistantRef = useCallback((ref) => {
    assistantRef.current = ref;
    // console.log(
    //   "AppFooter: Assistant component ref obtained.",
    //   ref ? "Instance received" : "Instance cleared"
    // );
  }, []); // Empty dependency array is correct

  // --- UI Elements ---
  const getCalendarButtonContent = () => {
    // (Unchanged)
    let iconColor, textColor;
    if (isLoading) {
      iconColor = textColor = "text-warning";
    } else if (!hasUnsavedChanges) {
      iconColor = textColor = "text-success";
    } else {
      iconColor = textColor = "text-secondary";
    }

    return (
      <>
        <FeatherIcon
          icon="calendar"
          width="15"
          height="15"
          className={iconColor}
        />
        <span className={`ms-2 ${textColor}`} style={{ fontSize: "0.8rem" }}>
          {formatDate(selectedDate || new Date())}
        </span>
      </>
    );
  };

  // Toggle button now ONLY controls the 'wakeWordEnabled' state
  const toggleWakeWordProcessing = (event) => {
    event.stopPropagation();
    const newState = !wakeWordEnabled;
    setWakeWordEnabled(newState);
    console.log(
      `AppFooter: Wake word processing manually ${
        newState ? "enabled" : "disabled"
      }.`
    );
    // If manually disabling, stop any potential audio playback/pending start
    if (!newState) {
      if (audioPlaybackRef.current) {
        console.log(
          "AppFooter: Manually disabling wake word, stopping audio playback."
        );
        audioPlaybackRef.current.pause();
        audioPlaybackRef.current.onended = null;
        audioPlaybackRef.current.onerror = null;
        audioPlaybackRef.current = null;
        // Ensure mic is unmuted
        if (currentStreamRef.current && currentStreamRef.current.active) {
          currentStreamRef.current
            .getAudioTracks()
            .forEach((track) => (track.enabled = true));
        }
      }
      setAssistantShouldListen(false); // Prevent assistant start if it was pending
    }
  };

  // --- Render ---
  return (
    <>
      {/* Always render AutoWakeWordLoader, control its processing via 'enabled' prop */}
      <AutoWakeWordLoader
        onModelStatusChange={setWakeWordModelLoaded}
        onWakeWordDetected={handleWakeWordDetected} // Pass the MODIFIED handler
        onStreamReady={handleStreamReady} // Pass the MODIFIED handler
        enabled={wakeWordEnabled} // Controls if AutoWakeWordLoader processes audio
      />

      <footer className="w-100 bg-primary-subtle">
        {" "}
        {/* Consider using navbarClass for consistency */}
        <div
          className="container-fluid d-flex justify-content-between align-items-center py-1"
          style={{ minHeight: "40px" }}
        >
          {/* Left Controls */}
          <div className="d-flex align-items-center">
            {showSidebarToggle && (
              <button
                className="btn btn-outline-secondary me-1 d-flex align-items-center border-0 p-1"
                onClick={toggleSidebar}
                style={{ height: "24px" }}
                aria-label="Toggle Sidebar"
              >
                <SidebarIcon size={16} />
              </button>
            )}

            {showCalendar && (
              <button
                className="btn d-flex align-items-center border-0 me-1 p-1"
                onClick={toggleCalendarVisibility}
                style={{ height: "24px" }}
                aria-label="Toggle Calendar Visibility"
              >
                {/* Using Lucide Calendar icon consistently */}
                <CalendarIcon
                  size={16}
                  className={
                    isLoading
                      ? "text-warning"
                      : !hasUnsavedChanges
                      ? "text-success"
                      : "text-secondary"
                  }
                />
                <span
                  className={`ms-1 ${
                    isLoading
                      ? "text-warning"
                      : !hasUnsavedChanges
                      ? "text-success"
                      : "text-secondary"
                  }`}
                  style={{ fontSize: "0.8rem" }}
                >
                  {formatDate(selectedDate || new Date())}
                </span>
              </button>
            )}

            {showSearch && (
              <button
                className="btn btn-outline-secondary d-flex align-items-center border-0 me-1 p-1"
                onClick={toggleSearchVisibility}
                style={{ height: "24px" }}
                aria-label="Toggle Search Visibility"
              >
                <SearchIcon size={16} />
              </button>
            )}

            {/* Microphone Toggle Button - Controls wake word *processing* */}
            {/* Only show if the model is loaded AND there's a valid stream */}
            {wakeWordModelLoaded && currentStreamRef.current && (
              <button
                className={`btn btn-outline-${
                  wakeWordEnabled ? "success" : "danger"
                } d-flex align-items-center border-0 me-1 p-1`}
                onClick={toggleWakeWordProcessing}
                style={{ height: "24px" }}
                aria-label={
                  wakeWordEnabled
                    ? "Disable Wake Word Processing"
                    : "Enable Wake Word Processing"
                }
                title={
                  wakeWordEnabled
                    ? "Disable Wake Word Processing"
                    : "Enable Wake Word Processing"
                }
              >
                {wakeWordEnabled ? <Mic size={16} /> : <MicOff size={16} />}
              </button>
            )}

            {/* Other Links (Done, Journal, Timeline) */}
            {showCompletedTasks && (
              <Link
                to="/CompletedTasksViewer"
                className="btn btn-outline-secondary d-flex align-items-center border-0 p-1 me-1"
                style={{ height: "24px" }}
                aria-label="View Completed Tasks"
              >
                <ListChecks size={16} />
              </Link>
            )}
            {showJournal && (
              <Link
                to="/journal"
                className="btn btn-outline-secondary d-flex align-items-center border-0 p-1 me-1"
                style={{ height: "24px" }}
                aria-label="View Journal"
              >
                <NotebookText size={16} />
              </Link>
            )}
            {showTimeLine && (
              <Link
                to="/timeline"
                className="btn btn-outline-secondary d-flex align-items-center border-0 p-1"
                style={{ height: "24px" }}
                aria-label="View Timeline"
              >
                <Clock size={16} />
              </Link>
            )}

            {/* Assistant Wrapper - Render conditionally */}
            {/* It renders based on showTaskPlexer state, which is controlled by wake word */}
            {/* or potentially manually if the 'showassistant' prop is true (though that flow isn't fully defined here) */}
            {(showassistant || showTaskPlexer) && (
              <AssistantWrapper
                // Pass the callback to get the inner Assistant's ref
                forwardedAssistantRef={handleAssistantRef}
                // sharedMediaStream prop is removed as Assistant effect uses streamInfo state
                handleDateClick={handleDateClick}
                reloadContent={reloadContent}
                isSidebarOpen={isSidebarOpen}
                sidebarWidth={sidebarWidth}
                showTaskPlexer={showTaskPlexer}
                setShowTaskPlexer={setShowTaskPlexer}
                onMicrophoneReleased={handleMicrophoneReleased} // Pass release callback
              />
            )}
          </div>

          {/* User Dropdown (Right) */}
          {user && showUserDropdown && (
            <div className="navbar-nav">
              <div className="nav-item dropdown">
                <span
                  ref={toggleRef}
                  className={`nav-link ${textClass}`}
                  onClick={toggleDropdown}
                  style={{ cursor: "pointer" }}
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                >
                  {user.username}
                  <FontAwesomeIcon
                    icon={faChevronUp}
                    className={`ms-2 transition-transform ${
                      dropdownOpen ? "rotate-180" : ""
                    }`} // Ensure transition CSS is defined
                  />
                </span>
                {dropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className={`dropdown-menu ${dropdownVisible ? "show" : ""}`}
                    style={{
                      position: "absolute",
                      top: "auto",
                      bottom: "100%", // Position above the trigger
                      right: 0,
                      left: "auto",
                      transform: `translateY(${
                        dropdownVisible ? "0" : "10px" // Animate from below slightly
                      })`,
                      opacity: dropdownVisible ? 1 : 0,
                      transition: "opacity 0.3s ease, transform 0.3s ease",
                      display: "block", // Needs display: block to be visible
                      minWidth: "200px",
                      zIndex: 1055, // Ensure it's above other elements like TaskPlexer overlay
                      boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)", // Shadow on top
                      borderRadius: "4px",
                      backgroundColor: theme === "light" ? "#fff" : "#343a40", // Use theme colors
                    }}
                    aria-label="User Menu"
                  >
                    {navItems.map((item) => (
                      <Link
                        key={item.name}
                        className="dropdown-item" // Add appropriate theme classes if needed
                        to={item.path}
                        onClick={toggleDropdown} // Close dropdown on click
                      >
                        <FontAwesomeIcon icon={item.icon} className="me-2" />
                        {item.name}
                      </Link>
                    ))}
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item" onClick={handleLogout}>
                      {" "}
                      {/* Add appropriate theme classes if needed */}
                      <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </footer>
    </>
  );
};

export default AppFooter;
